import { AuthenticationError } from '@superbet-group/web.lib.utilities';
import { forEach as _forEach } from 'lodash';
import { Dispatch } from 'vuex';

import store from '@app/store';
import { UserService } from '@core/services/user/UserService';
import StoreUtil from '@store/StoreUtil';

const SET_IS_ID_CARD_VERIFICATION_PENDING = 'SET_IS_ID_CARD_VERIFICATION_PENDING';
const SET_IS_BALANCE_LOADING = 'SET_IS_BALANCE_LOADING';
const SET_LAST_UPLOADED_KYC = 'SET_LAST_UPLOADED_KYC';
const CLEAR_ALL = 'CLEAR_ALL';

export interface IAccountState {
    // this field servers only for idCardVerification modal upload <=> accountContainer communication
    // accountContainer must hide upload button after modal idCard upload finishes upload!
    // this is because Leapbit/CT does not have proper status for idCardVerification pending!
    isIdCardVerificationPending: boolean;
    isBalanceLoading: boolean;
    lastUploadedKyc: FileList | null;
}

function defaultState(): IAccountState {
    return {
        isIdCardVerificationPending: false,
        isBalanceLoading: false,
        lastUploadedKyc: null,
    };
}

const accountStore = {
    namespaced: true,

    state: defaultState(),

    mutations: {
        [SET_IS_ID_CARD_VERIFICATION_PENDING]: StoreUtil.createSimpleMutator('isIdCardVerificationPending'),
        [SET_IS_BALANCE_LOADING]: StoreUtil.createSimpleMutator('isBalanceLoading'),
        [SET_LAST_UPLOADED_KYC]: StoreUtil.createSimpleMutator('lastUploadedKyc'),
        [CLEAR_ALL](state: any) {
            const _defaultState = defaultState();
            _forEach(_defaultState, (value, key) => {
                state[key] = value;
            });
        },
    },

    getters: {
        isIdCardVerificationPending: StoreUtil.createSimpleGetter('isIdCardVerificationPending'),
        isBalanceLoading: StoreUtil.createSimpleGetter('isBalanceLoading'),
        lastUploadedKyc: StoreUtil.createSimpleGetter('lastUploadedKyc'),
    },

    actions: {
        setIsIdCardVerificationPending: StoreUtil.createSimpleMutatorAction(SET_IS_ID_CARD_VERIFICATION_PENDING),
        setIsBalanceLoading: StoreUtil.createSimpleMutatorAction(SET_IS_BALANCE_LOADING),
        setLastUploadedKyc: StoreUtil.createSimpleMutatorAction(SET_LAST_UPLOADED_KYC),
        clearAll: StoreUtil.createSimpleMutatorAction(CLEAR_ALL),
        getBalance: StoreUtil.asyncErrorGuard(
            async (
                { dispatch }: { dispatch: Dispatch },
                options?: { showLoading: boolean; shouldCheckBonus: boolean },
            ) => {
                if (options?.showLoading) {
                    dispatch('setIsBalanceLoading', true);
                }

                try {
                    const balance = await UserService.getInstance().getPlayerBalance();
                    dispatch(
                        'data/authentication/setUserData',
                        {
                            key: 'balance',
                            value: balance,
                        },
                        { root: true },
                    );

                    // Check active bonus in order to show user correct bonus data and do correct bonus calculations
                    if (options?.shouldCheckBonus) {
                        dispatch(
                            'ui/user/bonuses/checkActiveBonuses',
                            null,
                            { root: true },
                        );
                    }
                } catch (e) {
                    if (e instanceof AuthenticationError) {
                        store.dispatch('data/authentication/sessionExpired', null, { root: true });
                        return;
                    }

                    if (options?.showLoading) {
                        dispatch('setIsBalanceLoading', false);
                    }
                    throw e;
                }

                if (options?.showLoading) {
                    dispatch('setIsBalanceLoading', false);
                }
            },
        ),
    },
};

export type AccountStore = typeof accountStore;

export default accountStore;
